<div class="job-photos-page">

  @if (isLoading) {
    <div class="empty-state">Loading job details...</div>
  } @else {

    <div class="top-summary-card">
      <div class="summary-left" style="display: flex; gap: 1rem; align-items: center; flex-wrap: wrap;">
        <div class="summary-item">
          <span class="summary-label">Total Selected</span>
          <strong>{{ totalCount }}</strong>
        </div>

        @if (jobUploadSessionId) {
          <div class="summary-divider"></div>
          <div class="summary-item text-success">
            <span class="summary-label">Confirmed</span>
            <strong>{{ uploadedCount }}</strong>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item text-warning">
            <span class="summary-label">Duplicates</span>
            <strong>{{ duplicateCount }}</strong>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item text-danger">
            <span class="summary-label">Failed</span>
            <strong>{{ failedCount }}</strong>
          </div>
        }

        @if (totalCount > 0) {
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-label">Showing</span>
            <strong>{{ startIndex }} – {{ endIndex }}</strong>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-label">Page</span>
            <strong>{{ currentPage }} / {{ totalPages }}</strong>
          </div>
        }
      </div>

      <div class="summary-actions" style="display: flex; gap: 0.5rem; align-items: center;">
        @if (!isPaymentCompleted && !isPartiallyPaid) {
          <div class="pay-now-wrap">
            <strong class="pay-amount">${{ upfrontCostOfJob | number:'1.2-2' }}</strong>
            <button type="button" class="pay-now-btn" [class.paid-success]="isPaymentCompleted" (click)="onPayNow()"
              [disabled]="isPaymentCompleted || isSaving">
              {{ paymentStatusText }}
            </button>
          </div>
        }
        <button
          type="button"
          class="save-btn"
          (click)="onStartUpload()"
          [disabled]="!isPaymentCompleted || isUploading || photos.length === 0"
        >
          @if (isUploading) {
            Uploading…
          } @else if (isAllUploaded || jobStatus === 'ImageUploadCompleted') {
            Submit Job for Processing
          } @else if (confirmedCount > 0 && pendingCount > 0) {
            Resume Upload
          } @else {
            Start Upload
          }
        </button>
      </div>
    </div>

    <div class="how-to-card">
      <div class="info-icon">i</div>
      <div class="how-to-content">
        <div class="how-to-title">Photos from your selected folder</div>
        <div class="how-to-text">
          The images below were loaded from the folder you selected during job setup.
          Click <strong>Start Upload</strong> to upload them directly to secure cloud storage.
        </div>
      </div>
    </div>

    @if (photos.length === 0) {
      <div class="empty-gallery-card">
        <div class="empty-icon">📂</div>
        <p>No images were found. Please go back and select a folder with images.</p>
      </div>
    } @else {
      <div class="gallery-card">
        <div class="gallery-header">
          <h2>Photo Gallery</h2>
          <span class="gallery-count">{{ totalCount }} image{{ totalCount !== 1 ? 's' : '' }}</span>
        </div>

        <div class="gallery-grid">
          @for (photo of pagedPhotos; track photo.id; let i = $index) {
            <div class="photo-tile" [ngClass]="'status-' + photo.uploadStatus">
              <div class="photo-index">#{{ startIndex + i }}</div>

              @if (photo.previewUrl) {
                <img [src]="photo.previewUrl" [alt]="photo.fileName" />
              } @else {
                <div class="photo-placeholder"></div>
              }

              <div class="photo-name">{{ photo.fileName }}</div>

              <!-- Per-file progress bar (shown while uploading) -->
              @if (photo.uploadStatus === 'uploading' && (photo.uploadProgress ?? 0) > 0) {
                <div class="file-progress-bar">
                  <div class="file-progress-fill" [style.width.%]="photo.uploadProgress"></div>
                </div>
              }

              <div class="photo-status" style="margin-top: 4px; font-size: 0.8em; font-weight: bold;">
                @if (photo.uploadStatus === 'pending')          { <span style="color: #6c757d;">Pending</span> }
                @if (photo.uploadStatus === 'preparing')        { <span style="color: #6f42c1;">Preparing…</span> }
                @if (photo.uploadStatus === 'uploading')        { <span style="color: #0d6efd;">Uploading {{ photo.uploadProgress ?? 0 }}%</span> }
                @if (photo.uploadStatus === 'uploadedToBlob' || photo.uploadStatus === 'uploaded_to_blob') { <span style="color: #20c997;">Confirming…</span> }
                @if (photo.uploadStatus === 'confirming')       { <span style="color: #20c997;">Confirming…</span> }
                @if (photo.uploadStatus === 'confirmed')        { <span style="color: #198754;">✓ Confirmed</span> }
                @if (photo.uploadStatus === 'skipped' || photo.uploadStatus === 'duplicate_skipped'){ <span style="color: #ffc107;">Skipped (duplicate)</span> }
                @if (photo.uploadStatus === 'failed')           { <span style="color: #dc3545;">✗ Failed</span> }
              </div>
            </div>
          }
        </div>

        @if (totalPages > 1) {
          <div class="pagination-bar">
            <button
              type="button"
              class="page-btn"
              [disabled]="currentPage === 1"
              (click)="goToPrevPage()"
            >
              ← Previous
            </button>

            <div class="page-info">
              Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
              &nbsp;·&nbsp; {{ startIndex }}–{{ endIndex }} of {{ totalCount }}
            </div>

            <button
              type="button"
              class="page-btn"
              [disabled]="currentPage === totalPages"
              (click)="goToNextPage()"
            >
              Next →
            </button>
          </div>
        }
      </div>
    }

  }

  @if (isUploading) {
    <div class="modal-overlay">
      <div class="flwx-upload-modal">
        <!-- Logo & Header -->
        <div class="flwx-modal-header">
          <div class="flwx-logo">
            <span class="logo-flw">flw</span><span class="logo-x">X</span><span class="logo-tm">™</span>
          </div>
          <h2 class="flwx-modal-title">Your Job Is Being Prepared for Upload</h2>
        </div>

        <div class="flwx-modal-body">
          <!-- 1. Preparation Card (Blue) -->
          <div class="flwx-card flwx-card-blue">
            <div class="card-icon-col">
              <div class="spinner-dots">
                <svg viewBox="0 0 24 24" fill="none" class="spin-icon">
                  <circle cx="12" cy="12" r="10" stroke="#93c5fd" stroke-width="3" stroke-dasharray="32" stroke-dashoffset="10" stroke-linecap="round"/>
                  <circle cx="12" cy="12" r="10" stroke="#2563eb" stroke-width="3" stroke-dasharray="16" stroke-dashoffset="0" stroke-linecap="round"/>
                </svg>
              </div>
            </div>
            <div class="card-content-col">
              <div class="card-title text-blue">flwX is preparing your images for upload...</div>
              <div class="card-subtitle">This can take 5–15 minutes for larger jobs.</div>
              <p class="card-description">
                During this preparation step, you may not see a progress bar or any visible activity on this page. This is normal. flwX is working in the background.
              </p>

              <!-- Preparation Progress Bar (Live Stats) -->
              @if (prepareTotal > 0) {
                <div class="progress-bar-container">
                  <div class="progress-bar-track">
                    <div class="progress-bar-fill blue-fill shimmer" [style.width.%]="preparePercent"></div>
                  </div>
                  <div class="progress-bar-meta">
                    <span>Analyzing &amp; Hashing: {{ prepareCurrent }} / {{ prepareTotal }}</span>
                    <span>{{ preparePercent }}%</span>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- 2. Warning Card (Yellow) -->
          <div class="flwx-card flwx-card-yellow">
            <div class="card-icon-col">
              <div class="icon-warning">
                <svg viewBox="0 0 24 24" fill="none" class="warn-triangle">
                  <path d="M12 2L1 21h22L12 2z" fill="#dc2626"/>
                  <path d="M12 9v5" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
                  <circle cx="12" cy="17" r="1.25" fill="#ffffff"/>
                </svg>
              </div>
            </div>
            <div class="card-content-col">
              <div class="card-title text-red">IMPORTANT — KEEP THIS PAGE OPEN</div>
              <div class="card-subtitle">While flwX prepares and uploads your job:</div>
              <ul class="card-bullets">
                <li>
                  <span class="bullet-badge badge-red">✕</span>
                  <span><strong>DO NOT</strong> close this browser tab or window.</span>
                </li>
                <li>
                  <span class="bullet-badge badge-red">✕</span>
                  <span><strong>DO NOT</strong> use your browser's Back button.</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- 3. Working Card (Green) -->
          <div class="flwx-card flwx-card-green">
            <div class="card-icon-col">
              <div class="icon-success">
                <svg viewBox="0 0 24 24" fill="none" class="check-circle">
                  <circle cx="12" cy="12" r="11" fill="#16a34a"/>
                  <path d="M7 12.5l3.5 3.5 7-7" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="card-content-col">
              <div class="card-title text-green">You CAN continue working</div>
              <div class="card-subtitle">You do not need to sit and watch this page. You can:</div>
              <ul class="card-bullets">
                <li>
                  <span class="bullet-badge badge-green">✓</span>
                  <span>Open another browser tab or window and continue working.</span>
                </li>
                <li>
                  <span class="bullet-badge badge-green">✓</span>
                  <span>Use other programs on your computer normally.</span>
                </li>
                <li>
                  <span class="bullet-badge badge-green">✓</span>
                  <span>Return to this page at any time to check your upload progress.</span>
                </li>
              </ul>

              <!-- Live Upload Progress Bar & Stats -->
              @if (uploadStep === 'uploading' || uploadCurrent > 0) {
                <div class="progress-bar-container upload-phase">
                  <div class="progress-bar-track">
                    <div class="progress-bar-fill green-fill shimmer" [style.width.%]="uploadPercent"></div>
                  </div>
                  <div class="progress-bar-meta">
                    <span>Uploaded: {{ uploadCurrent }} / {{ uploadTotal }}</span>
                    <span>{{ uploadPercent }}%</span>
                  </div>
                  <div class="progress-metrics-row">
                    @if (uploadSpeed) {
                      <span class="metric-pill">⚡ {{ uploadSpeed }}</span>
                    }
                    @if (uploadEta) {
                      <span class="metric-pill">⏳ {{ uploadEta }}</span>
                    }
                    <span class="metric-pill">4 Parallel Streams</span>
                    <span class="metric-pill stats-pill">{{ confirmedCount }} confirmed · {{ duplicateCount }} skipped</span>
                  </div>
                </div>
              } @else {
                <div class="card-notice-note">
                  <span class="info-badge">ℹ</span>
                  <span>The upload progress bar will appear automatically when the upload begins.</span>
                </div>
              }
            </div>
          </div>

          <!-- 4. Error Guide Card (Gray) -->
          <div class="flwx-card flwx-card-gray">
            <div class="card-icon-col">
              <div class="icon-gear">
                <svg viewBox="0 0 24 24" fill="none" class="gear-svg">
                  <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" fill="#475569"/>
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" stroke="#475569" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="card-content-col">
              <div class="card-title text-gray">If You See an Upload Error</div>
              <p class="card-description">
                Don't worry. Keep this page open and select the <strong>RESUME</strong> button.<br>
                flwX will continue the upload from where it left off.
              </p>
            </div>
          </div>
        </div>

        <!-- 5. Bottom Footer Banner -->
        <div class="flwx-modal-footer">
          <p class="footer-notice">Please keep this page open until flwX confirms that your upload is complete.</p>
        </div>
      </div>
    </div>
  }

  @if (showErrorAlert) {
    <div class="modal-overlay" (click)="closeAlert()">
      <div class="validation-alert-modal" (click)="$event.stopPropagation()">
        <div class="alert-modal-icon">⚠️</div>
        <h3>Alert</h3>
        <p class="alert-error-text">{{ errorMessage }}</p>
        <button type="button" class="alert-ok-btn" (click)="closeAlert()">Got It</button>
      </div>
    </div>
  }

</div>